import {
	Alert,
	Box,
	Button,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	Skeleton,
	TextField,
	Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { EmptySpace } from "@src/component/SignComponent";
import useGlobalDialog from "@src/hook/useGlobalDialog";
import useLoader from "@src/hook/useLoader";
import useUser from "@src/hook/useUser";
import { globalSnackbarState } from "@src/state";
import { useRouter } from "next/router";
import { Controller, useForm } from "react-hook-form";
import { useSetRecoilState } from "recoil";

import styled from "@emotion/styled";
import { acceptProposal, getProposalDetail, rejectProposalByClient, cancelProposalAccept, sendProposalAssistantFeedback } from "@src/api/match";
import Rating from "@mui/material/Rating";
import { ApprovalProposalRequest, ProposalResponse } from "@src/api/types";
import { refundDepositForClient } from "@src/api/match-deposit";
import { getDepositStatus } from "@src/api/match-deposit";
import moment from "moment";
import "moment/locale/ko";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Line, TitleWrapper, Wrapper, WrapperContainer } from "../base/BasicFormWrapper";

import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { GroupA } from "@src/ruix";
import { numberWithCommasFromNumber } from "@src/utils/CalculationUtils";
import Linkify from "linkify-react";
import { OnePointDialog } from "../base/OnePointDialog";
import useProposalAssistant from "@src/hook/useProposalAssistant";
import ProposalAssistantReport, { SectionToggle, SummaryToggle } from "./ProposalAssistantReport";

type ProposalAcceptForm = Omit<ApprovalProposalRequest, "matchUuid" | "proposalUuid">;

interface ProposalProps {
	uuid: string;
	isExpert: string;
	darkTheme?: boolean;
	matchedExpertDataUuid: string;
	exportUuid?: string;
	isLastOne?: boolean;
}

const rejectReason = [
	"역량이나 전문성에 확신이 없어요",
	"적극성이나 의지가 부족해보여요",
	"저희 회사와는 컬쳐핏이 맞지 않아요",
	"업무 방식과 시간대 등 조건이 맞지 않아요",
	"최종 제안 금액이 높아 부담돼요",
	"전문가는 만족스럽지만 내부 사정이 변경되었어요",
	"기타",
];

const splitReasonLeadAndEffect = (point: string) => {
	const normalized = String(point || "").replace(/\r/g, "\n");
	const dashed = normalized.match(/^(.*?)\s+-\s+(.+)$/);
	if (dashed) return { lead: dashed[1].trim(), tail: dashed[2].trim() };
	const lines = normalized.split(/\n+/g).map((l) => l.trim()).filter(Boolean);
	if (lines.length >= 2) return { lead: lines[0], tail: lines.slice(1).join(" ") };
	return { lead: normalized.trim(), tail: "" };
};

const fromTextToLines = (value?: string | null, max = 5) => {
	const raw = String(value || "").replace(/\r/g, "\n").trim();
	if (!raw) return [];
	return raw.split(/\n|•/g).map((l) => l.replace(/^[-•·]\s*/, "").trim()).filter(Boolean).slice(0, max);
};

const ProposalView = (props: ProposalProps) => {
	const router = useRouter();
	const { uuid, isExpert, darkTheme, matchedExpertDataUuid, exportUuid, isLastOne } = props;
	const [open, setOpen] = useState(false);
	const [openDepositRefundModal, setOpenDepositRefundModal] = useState(false);
	const [isAiOpen, setIsAiOpen] = useState(false);
	const [openAiFeedbackModal, setOpenAiFeedbackModal] = useState(false);
	const [aiFeedbackRating, setAiFeedbackRating] = useState<number | null>(null);
	const [aiFeedbackComment, setAiFeedbackComment] = useState("");
	const [aiFeedbackSubmitted, setAiFeedbackSubmitted] = useState(false);
	const queryClient = useQueryClient();
	const [floatingButtonWidth, setFloatingButtonWidth] = useState<number | undefined>(0);
	const { user } = useUser();

	const {
		handleSubmit,
		control,
		setValue,
		setError,
		clearErrors,
		formState,
		getFieldState,
		reset,
		getValues,
		watch,
	} = useForm<ProposalAcceptForm>({
		defaultValues: {
			desiredStartDate: new Date(),
			useSupportFund: null,
			supportInfo: null,
			understandSupportFund: false,
		},
		mode: "onBlur",
	});

	const {
		handleSubmit: rejectHandleOnSubmit,
		formState: { errors },
		control: controlReject,
		reset: resetReject,
		watch: watchReject,
	} = useForm<{
		formResponse: string;
		etc: string;
	}>({
		reValidateMode: "onChange",
		defaultValues: {
			formResponse: "",
			etc: "",
		},
	});

	const useSupportFund = watch("useSupportFund");

	useEffect(() => {
		if (useSupportFund === false) {
			setValue("supportInfo", null);
		}
	}, [useSupportFund]);

	const withLoader = useLoader();
	const { openDialog } = useGlobalDialog();
	const setSnackbareState = useSetRecoilState(globalSnackbarState);

	const [openRejectModal, setOpenRejectModal] = useState(false);

	const {
		mutate: mutateHandleOnClickRejectByClient,
		isLoading: isLoadingHandleOnClickRejectByClient,
	} = useMutation(rejectProposalByClient, {
		onSuccess: (data) => {
			if (data.isSuccess) {
				// 마지막 거절일 때 디파짓 환불 완료 모달 표시
				if (isLastOne) {
					setOpenRejectModal(false);
					setOpenDepositRefundModal(true);
					setSnackbareState("제안서가 거절되었습니다.");
					setTimeout(() => {
						setSnackbareState("");
					}, 3000);
				} else {
					// 일반 거절인 경우 모달 닫고 페이지 이동
					setOpenRejectModal(false);
					setSnackbareState("제안서가 거절되었습니다. 3초 후 메인 페이지로 이동합니다.");
					setTimeout(() => {
						router.replace("/match/my");
					}, 3000);
				}
			} else {
				setSnackbareState("죄송합니다. 채널톡으로 문의 부탁드립니다.");
			}
		},
		onError: () => {
			setSnackbareState("죄송합니다. 채널톡으로 문의 부탁드립니다.");
		},
	});

	const {
		mutate: mutateCancelProposalAccept,
		isLoading: isLoadingCancelProposalAccept,
	} = useMutation(cancelProposalAccept, {
		onSuccess: (data) => {
			if (data.isSuccess) {
				setSnackbareState("제안서 수락이 취소되었습니다. 원래 상태로 복구되었습니다.");
				setProposal({
					...proposal,
					isAccepted: "",
				});
				// 페이지 새로고침하여 최신 상태 반영
				setTimeout(() => {
					window.location.reload();
				}, 2000);
			} else {
				setSnackbareState("죄송합니다. 채널톡으로 문의 부탁드립니다.");
			}
		},
		onError: () => {
			setSnackbareState("죄송합니다. 채널톡으로 문의 부탁드립니다.");
		},
	});

	const [proposal, setProposal] = useState<ProposalResponse>({
		matchUuid: "",
		proposalUuid: "",
		fileUrl: "",
		client: "",
		expert: "",
		purpose: "",
		plan: "",
		timeopt: "",
		cost: "",
		expertCost: "",
		comment: "",
		isAccepted: "",
		desiredStartDate: "",
		serviceType: "",
		dateRequestConfirmProposal: null,
		dateAcceptDeadline: null,
		costTen: 0,
		costFifteen: 0,
		costTwenty: 0,
		workType: "",
		workTypeComment: "",
	});

	useEffect(() => {
		if (proposal.desiredStartDate) {
			setValue("desiredStartDate", new Date(proposal.desiredStartDate));
		}
	}, [proposal.desiredStartDate]);

	const { data } = useQuery(
		["getProposalData", uuid],
		async () =>
			await withLoader(
				getProposalDetail(uuid as string, matchedExpertDataUuid || ""),
				"로딩 중입니다\n1~3초 정도 소요됩니다",
			),
		{
			refetchOnWindowFocus: false, // react-query는 사용자가 사용하는 윈도우가 다른 곳을 갔다가 다시 화면으로 돌아오면 이 함수를 재실행합니다. 그 재실행 여부 옵션 입니다.
			refetchOnMount: false,
			retryOnMount: false,
			staleTime: Infinity,
			cacheTime: Infinity,
			onError: (error: any) => {
				setSnackbareState(error.errorMessage || error.message);
				router.replace("/");
			},
		},
	);

	const { data: depositStatus } = useQuery(
		["depositStatus", proposal.matchUuid],
		() => getDepositStatus(proposal.matchUuid),
		{ 
			enabled: !!proposal.matchUuid,
			refetchOnWindowFocus: false,
			retry: false,
			staleTime: 50000,
			cacheTime: 50000,
		}
	);

	const proposalAssistant = useProposalAssistant(
		!isExpert && proposal.matchUuid ? proposal.matchUuid : undefined,
	);

	useEffect(() => {
		if (data) {
			setProposal(data);
		}
	}, [data]);

	useEffect(() => {
		setTimeout(() => handleWidth(), 200);
		window.addEventListener("resize", handleWidth);
		return () => {
			window.removeEventListener("resize", handleWidth);
		};
	}, []);

	const handleWidth = () => {
		setFloatingButtonWidth(
			document.getElementById("proposal-wrapper")?.offsetWidth || window.innerWidth,
		);
	};

	const handleOnClickAccept = async (data: ProposalAcceptForm) => {
		try {
			setOpen(false);
			const result = await withLoader(
				acceptProposal({
					...data,
					matchUuid: proposal.matchUuid,
					proposalUuid: proposal.proposalUuid,
					matchedExpertDataUuid: matchedExpertDataUuid,
				}),
				"전송 중",
			);
			if (result.isSuccess) {
				// depositUuid가 존재하는 경우만 환불 모달 표시
				if (depositStatus?.depositUuid) {
					setOpenDepositRefundModal(true);
				}

				setSnackbareState(
					"제안 수락이 완료되었습니다!\n이메일과 알림톡으로 이후 프로세스를 안내드리겠습니다",
				);
				setProposal({
					...proposal,
					isAccepted: "TRUE",
				});

				// AI 후보자 분석 리포트가 있으면 피드백 모달 표시
				if (proposalAssistant.report) {
					setOpenAiFeedbackModal(true);
				}

				setTimeout(() => {
					queryClient.refetchQueries(["getMatchs"]);
					window.open("https://notion.1point.kr/referral");
				}, 2000);
			}
		} catch (error: any) {
			setSnackbareState(error.errorMessage || error.message);
		}
	};

	const isValidDate = moment(proposal.desiredStartDate).isValid();

	if (!proposal.proposalUuid) {
		return null;
	}

	return (
		<>
			<>
				<Wrapper 
					style={{ 
						gap: 20,
						padding: "20px"
					}} 
					id={"proposal-wrapper"}
				>
					{/* depositUuid가 존재하는 경우만 보증금 환불 안내 표시 - 고객사만 볼 수 있음 */}
					{depositStatus?.depositUuid && user.isLogin && user.userType === 1 && (
						<div 
							style={{
								backgroundColor: "#fff3cd",
								border: "1px solid #ffeaa7",
								borderRadius: "12px",
								padding: "20px",
								marginBottom: "24px",
								boxShadow: "0 2px 8px rgba(255, 193, 7, 0.15)"
							}}
						>
							<div style={{ 
								display: "flex", 
								alignItems: "center", 
								gap: "12px",
								marginBottom: "8px"
							}}>
								<div style={{
									width: "24px",
									height: "24px",
									backgroundColor: "#ffc107",
									borderRadius: "50%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									fontSize: "14px",
									color: "#fff",
									fontWeight: "bold"
								}}>
									💡
								</div>
								<div style={{
									fontSize: "16px",
									fontWeight: "700",
									color: "#856404"
								}}>
									서비스 보증금 환불 안내
								</div>
							</div>
							<div style={{
								fontSize: "14px",
								lineHeight: "1.6",
								color: "#856404",
								paddingLeft: "36px"
							}}>
								전문가 고용 또는 모든 후보 거절 시 3일 이내에 환불이 완료됩니다.
								<br />
								서비스 진행을 하지 않으시더라도 모든 후보를 거절하셔야 환불이 진행됩니다.
							</div>
						</div>
					)}
					
					<WrapperContainer style={{ marginBottom: "24px" }}>
						<TitleWrapper>
							<div className="top">
								<div>
									<div id="top-title" style={{ fontSize: "24px", fontWeight: "700" }}>
										{`전문가 ${proposal.expert}`}
									</div>
								</div>
							</div>
						</TitleWrapper>
						<EmptySpace space={24} />
						
						{/* 정보 박스 */}
						<div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
							{proposal?.desiredStartDate && (
								<div style={{
									flex: 1,
									minWidth: "200px",
									backgroundColor: "#f8f9fa",
									padding: "14px 18px",
									borderRadius: "8px",
									border: "1px solid #e9ecef",
								}}>
									<div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "4px", fontWeight: 500 }}>업무 시작 가능일</div>
									<div style={{ fontSize: "15px", fontWeight: 700, color: "#212529" }}>
										{moment(proposal.desiredStartDate).format("YYYY.MM.DD")} ({moment(proposal.desiredStartDate).format("ddd")})
									</div>
								</div>
							)}
							{proposal?.dateAcceptDeadline && (
								<div style={{
									flex: 1,
									minWidth: "200px",
									backgroundColor: "#fef3c7",
									padding: "14px 18px",
									borderRadius: "8px",
									borderLeft: "4px solid #f59e0b",
								}}>
									<div style={{ fontSize: "12px", color: "#92400e", marginBottom: "4px", fontWeight: 500 }}>제안서 검토 기한</div>
									<div style={{ fontSize: "15px", fontWeight: 700, color: "#b45309" }}>
										{moment(proposal.dateAcceptDeadline).format("YYYY.MM.DD")} ({moment(proposal.dateAcceptDeadline).format("ddd")})
									</div>
								</div>
							)}
						</div>
						
						{/* 후보자 비교 분석 AI (고객사만) — 아코디언 */}
						{!isExpert && (() => {
							const expertReport = proposalAssistant.expertReportMap.get(matchedExpertDataUuid);
							const suitLines = fromTextToLines(expertReport?.suitabilityReason, 5);
							const proposalLines = fromTextToLines(expertReport?.proposalHighlights, 3);
							const interviewLines = fromTextToLines(expertReport?.interviewInsights, 3);
							const hasContent = suitLines.length > 0 || proposalLines.length > 0 || interviewLines.length > 0;

							return (
								<Box sx={{
									mb: 2.5,
									border: "1.5px solid #d4bffc",
									borderRadius: "12px",
									background: "linear-gradient(180deg, rgba(243,236,255,0.95) 0%, rgba(250,247,255,0.95) 100%)",
									boxShadow: "0 2px 8px rgba(124, 58, 237, 0.08)",
									overflow: "hidden",
								}}>
									<Button
										fullWidth
										variant="text"
										onClick={() => setIsAiOpen((prev) => !prev)}
										startIcon={<AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: "#7c3aed" }} />}
										endIcon={isAiOpen
											? <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
											: <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
										}
										sx={{
											width: "100%",
											border: "none",
											borderRadius: isAiOpen ? "12px 12px 0 0" : "12px",
											background: "transparent",
											justifyContent: "space-between",
											textTransform: "none",
											px: 1.25,
											py: 0.9,
											minHeight: 44,
											"& .MuiButton-startIcon": { marginRight: "6px" },
											"& .MuiButton-endIcon": { marginLeft: "6px", color: "#7c3aed" },
											"&:hover": { background: "rgba(243,236,255,0.45)" },
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#5b21b6", lineHeight: 1.35 }}>
												AI 후보자 분석
											</Typography>
											{!isAiOpen && (
												<Typography sx={{
													fontSize: "11px",
													color: "#7c3aed",
													backgroundColor: "rgba(124, 58, 237, 0.08)",
													px: 0.8,
													py: 0.2,
													borderRadius: "4px",
													fontWeight: 600,
												}}>
													Click
												</Typography>
											)}
										</Box>
									</Button>

									<Collapse in={isAiOpen} timeout={220}>
										<Box sx={{ width: "100%", borderTop: "1px solid #e9d5ff", backgroundColor: "#faf8ff", p: 1.35 }}>
											{proposalAssistant.generationError ? (
												<Alert severity="warning" sx={{ width: "100%", mb: 1.25 }}>{proposalAssistant.generationError}</Alert>
											) : null}

											{(proposalAssistant.isLoading || proposalAssistant.isGenerating) && !expertReport ? (
												<Box sx={{ textAlign: "center", py: 2 }}>
													<Box sx={{
														display: "inline-block",
														width: 28,
														height: 28,
														border: "3px solid #e9d5ff",
														borderTop: "3px solid #7c3aed",
														borderRadius: "50%",
														animation: "spin 0.8s linear infinite",
														"@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
														mb: 1.2,
													}} />
													<Typography sx={{ fontSize: "13.5px", color: "#5b21b6", fontWeight: 600 }}>
														AI가 제안서를 분석하고 있습니다...
													</Typography>
													<Typography sx={{ fontSize: "12px", color: "#9ca3af", mt: 0.5 }}>
														약 30초~1분 정도 소요됩니다
													</Typography>
												</Box>
											) : null}

											{!proposalAssistant.isLoading && !proposalAssistant.isGenerating && !expertReport ? (
												<Box sx={{ textAlign: "center", py: 1.5 }}>
													<Typography sx={{ fontSize: "13.5px", color: "#4b5563", lineHeight: 1.6, mb: 1.2 }}>
														AI가 이 전문가의 제안서를 분석해 드립니다.
													</Typography>
													<Button
														variant="contained"
														size="small"
														onClick={() => proposalAssistant.generateReport(false, matchedExpertDataUuid)}
														disabled={proposalAssistant.isGenerating}
														sx={{
															borderRadius: "8px",
															fontWeight: 700,
															fontSize: 13,
															backgroundColor: "#7c3aed",
															"&:hover": { backgroundColor: "#6d28d9" },
														}}
													>
														{proposalAssistant.isGenerating ? "분석 중..." : "AI 분석 시작"}
													</Button>
												</Box>
											) : null}

											{hasContent ? (
												<>
													<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
														{/* 컨설턴트 코멘트 */}
														{expertReport?.consultantComment ? (
															<Box sx={{
																px: 1.25,
																py: 1,
																borderRadius: "10px",
																backgroundColor: "#f8fafc",
																border: "1px solid #e5e7eb",
															}}>
																<Typography sx={{
																	fontSize: 13.5,
																	color: "#374151",
																	lineHeight: 1.7,
																}}>
																	{expertReport.consultantComment}
																</Typography>
															</Box>
														) : null}
														{/* Summary 토글 */}
														{expertReport?.expertHeadline && (
															<SummaryToggle
																headline={expertReport.expertHeadline}
																decisionGuide={expertReport.expertDecisionGuide}
																expertId={matchedExpertDataUuid}
															/>
														)}
														<SectionToggle
															label="프로필 · 인터뷰 인사이트"
															color="#3b82f6"
															bgColor="#eff6ff"
															points={[...suitLines, ...interviewLines]}
															expertId={matchedExpertDataUuid}
														/>
														<SectionToggle
															label="제안서 핵심 포인트"
															color="#8b5cf6"
															bgColor="#f5f3ff"
															points={proposalLines}
															expertId={matchedExpertDataUuid}
														/>
													</Box>
													</>
											) : null}
										</Box>
									</Collapse>
								</Box>
							);
						})()}

						{/* 업무 목표 및 범위 */}
						<Row
							style={{
								gap: 16,
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
								marginBottom: "20px"
							}}
						>
							<div className="detail-title" style={{ fontSize: "20px", fontWeight: "700", color: "#212529" }}>
								업무 목표 및 범위
							</div>
							<Line />
						</Row>
						<div className="desc" style={{ 
							lineHeight: "1.6", 
							color: "#495057",
							marginBottom: "16px",
							whiteSpace: "pre-line"
						}}>
							{proposal?.purpose}
						</div>

						<div className="desc" style={{ 
							lineHeight: "1.6", 
							color: "#495057",
							marginBottom: "16px",
							whiteSpace: "pre-line"
						}}>
							<Linkify
								as="span"
								options={{
									className: "linkify",
									target: "_blank",
								}}
							>
								{proposal?.plan}
							</Linkify>
						</div>
						
						{/* 참고 사항 */}
						{(proposal.comment ||
							proposal.fileUrl ||
							proposal.workType ||
							proposal.workTypeComment) && (
							<>
								<EmptySpace space={24} />
								<Row
									style={{
										gap: 16,
										flexDirection: "column",
										justifyContent: "flex-start",
										alignItems: "flex-start",
										marginBottom: "20px"
									}}
								>
									<div className="detail-title" style={{ fontSize: "20px", fontWeight: "700", color: "#212529" }}>
										참고 사항
									</div>
									<Line />
								</Row>
								<div className="desc" style={{ 
									lineHeight: "1.6", 
									color: "#495057",
									whiteSpace: "pre-line"
								}}>
									{`${
										proposal.workType
											? `${proposal.workType}\n${
													proposal.workTypeComment
														? `${proposal.workTypeComment}\n`
														: ""
											  }\n`
											: ""
									}`}
									{proposal.comment ? (
										<Linkify
											as="span"
											options={{
												className: "linkify",
												target: "_blank",
											}}
										>
											{proposal.comment}
										</Linkify>
									) : (
										""
									)}
									{`\n`}
									{proposal.fileUrl && <a href={proposal.fileUrl} style={{ color: "#007bff", textDecoration: "underline" }}>참고자료</a>}
								</div>
							</>
						)}
					</WrapperContainer>
					{/* 서비스 이용 요금 */}
					<WrapperContainer style={{ 
						backgroundColor: "#ffffff", 
						borderRadius: "12px", 
						padding: "24px",
						boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
						marginBottom: "24px"
					}}>
						{/* 고객사에게만 제목 표시 */}
						{!isExpert && (
							<>
								<TitleWrapper>
									<div className="top">
										<div>
											<div id="top-title" style={{ fontSize: "22px", fontWeight: "700", color: "#212529" }}>
												서비스 요금
											</div>
										</div>
									</div>
								</TitleWrapper>
								<EmptySpace space={24} />
							</>
						)}
						
						{/* 전문가용 - 간단한 3가지 정보만 표시 */}
						{isExpert ? (
							<>
								<div style={{ 
									backgroundColor: "#f8f9fa", 
									padding: "16px", 
									borderRadius: "8px",
									border: "1px solid #e9ecef"
								}}>
									<Row style={{ justifyContent: "space-between", marginBottom: "12px" }}>
										<div style={{ fontWeight: "600", color: "#495057" }}>서비스 유형</div>
										<div style={{ color: "#6c757d" }}>{proposal.serviceType}</div>
									</Row>
									{proposal.serviceType === "시간제" && (
										<Row style={{ justifyContent: "space-between", marginBottom: "12px" }}>
											<div style={{ fontWeight: "600", color: "#495057" }}>시간제 플랜</div>
											<div style={{ color: "#6c757d" }}>
												{proposal.timeopt === "0시간" ? (
													<div style={{ color: "#6c757d" }}>개별 협의</div>
												) : (
													<div>{`주 ${proposal.timeopt}`}</div>
												)}
											</div>
										</Row>
									)}
								<Row style={{ justifyContent: "space-between" }}>
									<div style={{ fontWeight: "600", color: "#495057" }}>전문가 대금</div>
									<div style={{ fontSize: "18px", fontWeight: "700", color: "#007bff" }}>
										{numberWithCommasFromNumber(Number(proposal.expertCost || 0) * 10000)}원
										{(proposal.serviceType === "시간제") &&
											` (${uuid === "yu58enj116hbmxnvyw30ryu58y4z9pao" ? "6주" : "4주"})`}
									</div>
								</Row>
								</div>
							</>
						) : (
							<>
								{/* 고객사용 - 상세한 정보 표시 */}
								{/* 서비스 내역 */}
								<div style={{ marginBottom: "24px" }}>
									<div style={{ fontSize: "18px", fontWeight: "600", color: "#495057", marginBottom: "16px" }}>
										서비스 내역
									</div>
									<Line style={{ marginBottom: "16px" }} />
									
									<div style={{ 
										backgroundColor: "#f8f9fa", 
										padding: "16px", 
										borderRadius: "8px",
										border: "1px solid #e9ecef"
									}}>
										<Row style={{ justifyContent: "space-between", marginBottom: "12px" }}>
											<div style={{ fontWeight: "600", color: "#495057" }}>전문가명</div>
											<div style={{ color: "#6c757d" }}>{proposal.expert}</div>
										</Row>
										<Row style={{ justifyContent: "space-between", marginBottom: "12px" }}>
											<div style={{ fontWeight: "600", color: "#495057" }}>서비스 유형</div>
											<div style={{ color: "#6c757d" }}>{proposal.serviceType}</div>
										</Row>
										{proposal.serviceType === "시간제" && (
											<Row style={{ justifyContent: "space-between" }}>
												<div style={{ fontWeight: "600", color: "#495057" }}>시간제 플랜</div>
												<div style={{ color: "#6c757d" }}>
													{proposal.timeopt === "0시간" ? (
														<div style={{ color: "#6c757d" }}>개별 협의</div>
													) : (
														<div>{`주 ${proposal.timeopt}`}</div>
													)}
												</div>
											</Row>
										)}
									</div>
								</div>

								{/* 서비스 요금 */}
								<div style={{ marginBottom: "24px" }}>
									<Row style={{ justifyContent: "space-between", marginBottom: "16px" }}>
										<div style={{ fontSize: "18px", fontWeight: "600", color: "#495057" }}>
											서비스 요금
										</div>
										<div style={{ fontSize: "20px", fontWeight: "700", color: "#007bff" }}>
											{numberWithCommasFromNumber(Number(proposal.cost || 0) * 10000)}원
											{(proposal.serviceType === "시간제") &&
												` (${uuid === "yu58enj116hbmxnvyw30ryu58y4z9pao" ? "6주" : "4주"})`}
										</div>
									</Row>
									<Line style={{ marginBottom: "16px" }} />
									<Row style={{ justifyContent: "flex-end" }}>
										<div style={{ color: "#6c757d", fontSize: "14px" }}>VAT 10% 별도</div>
									</Row>
								</div>

								{/* 추가 옵션 */}
								{proposal.costTen || proposal.costFifteen || proposal.costTwenty ? (
									<div style={{ marginBottom: "24px" }}>
										<div style={{ fontSize: "18px", fontWeight: "600", color: "#495057", marginBottom: "16px" }}>
											참고사항
										</div>
										<div style={{ 
											color: "#6c757d", 
											lineHeight: "1.5",
											marginBottom: "16px"
										}}>
											다른 투입 시간으로 진행을 희망하신다면 원포인트에 조율 요청하기를 클릭해주세요.
										</div>
										<Line style={{ marginBottom: "16px" }} />
										
										<div style={{ 
											backgroundColor: "#f8f9fa", 
											padding: "16px", 
											borderRadius: "8px",
											border: "1px solid #e9ecef"
										}}>
											{proposal.costTen && (
												<Row style={{ justifyContent: "space-between", marginBottom: "8px" }}>
													<div style={{ color: "#495057" }}>주 10시간</div>
													<div style={{ color: "#6c757d" }}>{proposal.costTen}만원 / 4주</div>
												</Row>
											)}
											{proposal.costFifteen && (
												<Row style={{ justifyContent: "space-between", marginBottom: "8px" }}>
													<div style={{ color: "#495057" }}>주 15시간</div>
													<div style={{ color: "#6c757d" }}>{proposal.costFifteen}만원 / 4주</div>
												</Row>
											)}
											{proposal.costTwenty && (
												<Row style={{ justifyContent: "space-between" }}>
													<div style={{ color: "#495057" }}>주 20시간</div>
													<div style={{ color: "#6c757d" }}>{proposal.costTwenty}만원 / 4주</div>
												</Row>
											)}
										</div>
										
										<EmptySpace space={20} />
										<CustomButton
											className="btn"
											fullWidth
											variant="contained"
											onClick={() => {
												openDialog({
													title: "상담 창으로 이동합니다.",
													message: "",
													desc: "상담 채널에서 조율이 필요한 내용을 말씀해주세요.",
													btn2Text: "확인하기",
													onClickBtn: async () => {
														window.open("https://1point.channel.io/home");
													},
												});
											}}
											style={{
												border: "2px solid var(--primary)",
												color: "var(--primary)",
												backgroundColor: "transparent",
												minHeight: "48px",
												fontSize: "14px",
												fontWeight: "600",
												borderRadius: "8px"
											}}
										>
											원포인트에 조율 요청하기
										</CustomButton>
									</div>
								) : null}
								
								{/* 장기 계약 할인 */}
								{proposal.serviceType !== "프로젝트" && (
									<div style={{ 
										backgroundColor: "#e3f2fd", 
										padding: "16px", 
										borderRadius: "8px",
										border: "1px solid #2196f3",
										textAlign: "center"
									}}>
										<div style={{ color: "#1976d2", fontSize: "14px", fontWeight: "600" }}>
											장기 계약으로 <a
												href="https://docs.channel.io/onepoint/ko/articles/ff6c6176-장기-협업장기계약"
												target="_blank"
												rel="noreferrer"
												style={{
													color: "#1976d2",
													textDecoration: "underline",
													cursor: "pointer",
												}}
											>
												5% 추가할인
											</a> 받아보세요!
										</div>
									</div>
								)}
							</>
						)}
					</WrapperContainer>
					{/* 버튼 영역 - 상단으로 이동 - 고객사만 볼 수 있음 */}
					{matchedExpertDataUuid && !proposal.isAccepted && user.isLogin && user.userType === 1 && (
						<div
							style={{
								width: "100%",
								maxWidth: "100%",
								backgroundColor: "#ffffff",
								padding: "24px",
								borderRadius: "16px",
								boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
								border: "1px solid #e9ecef",
								marginBottom: "24px"
							}}
						>
							<Column style={{ gap: 20 }}>
								{/* 고용 확정 안내 */}
								<div
									style={{
										backgroundColor: "#f8f9fb",
										padding: "12px 16px",
										borderRadius: "8px",
										border: "1px solid #e9ecef",
										fontSize: "13px",
										lineHeight: 1.6,
										color: "#5b6473",
										wordBreak: "keep-all",
									}}
								>
									전문가님 한 분과 협업이 확정되면 다른 전문가님들의 매칭은 자동으로 종료됩니다. 추가 고용을 원하시는 경우, 오른쪽 하단의 '문의하기'를 이용하여 말씀해 주시면 감사하겠습니다.
								</div>
								{/* 첫 번째 버튼 행 */}
								<Row style={{ gap: 16 }}>
									<CustomButton
										className="btn"
										fullWidth
										variant="contained"
										onClick={() => setOpen(true)}
										style={{
											border: "none",
											backgroundColor: "var(--primary)",
											color: "white",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											minHeight: "52px",
											fontSize: "15px",
											fontWeight: "600",
											borderRadius: "12px",
											transition: "all 0.2s ease"
										}}
									>
										고용하기
									</CustomButton>
								</Row>
								
								{/* 두 번째 버튼 행 */}
								{/* 두 번째 버튼 행 */}
								<Row>
									{!proposal.costTen &&
										!proposal.costFifteen &&
										!proposal.costTwenty ? (
										<NegotiationButton
											className="btn"
											fullWidth
											variant="contained"
											onClick={() => {
												openDialog({
													title: "상담 창으로 이동합니다.",
													message: "",
													desc: "상담 채널에서 조율이 필요한 내용을 말씀해주세요.",
													btn2Text: "확인하기",
													onClickBtn: async () => {
														window.open(
															"https://1point.channel.io/home",
														);
													},
												});
											}}
										>
											원포인트에 조율 요청하기
										</NegotiationButton>
									) : null}
									<NegotiationButton
										className="btn"
										fullWidth
										variant="contained"
										onClick={() => {
											setOpenRejectModal(true);
										}}
									>
										거절하기
									</NegotiationButton>
									
								</Row>
							</Column>
						</div>
					)}
					<EmptySpace
						space={
							!proposal.isAccepted &&
							matchedExpertDataUuid &&
							user.isLogin &&
							user.userType === 1
								? 96
								: 32
						}
					/>
				</Wrapper>
			</>

			<ProposalAcceptDialog
				maxWidth={"md"}
				fullWidth
				open={open}
				sx={{
					zIndex: 20000,
				}}
				onClose={() => setOpen(false)}
			>
				<form
					onSubmit={handleSubmit((data) => {
						handleOnClickAccept(data);
					})}
				>
					<DialogTitle>
						<div className="title-box">
							<div className="title">{`제안서 수락을 위한\n마지막 단계입니다!`}</div>
							<div className="icon">
								<img className="logo" src={"/static/image/logo_white.svg"} />
							</div>
						</div>
					</DialogTitle>
					<DialogContent>
						<div className="description">
							{isValidDate ? (
								<>
									{`전문가님의 업무 시작 가능일은 `}
									<span className="blue">
										{moment(proposal.desiredStartDate).format("M월 D일")}
									</span>
									{`입니다.\n해당 날짜 이후 일자 중 업무 시작일을 선택해주세요.`}
								</>
							) : (
								<>업무 시작일을 선택해주세요.</>
							)}
						</div>
						<div className="description-mini">
							{`• 선택하신 업무 시작일 전까지 계약서 서명과 서비스 대금 결제가 진행되어야 하는 점 참고해주세요\n• 선택 가능 일자를 벗어나는 날짜에 업무를 시작하고자 하시는 경우 원포인트 채널톡으로 별도 문의해주세요`}
						</div>
						<EmptySpace space={10} />
						<LocalizationProvider
							dateFormats={{ monthShort: "MM", monthAndYear: "yyyy.MM" }}
							dateAdapter={AdapterMoment}
						>
							<Controller
								name={"desiredStartDate"}
								control={control}
								defaultValue={new Date()}
								rules={{
									required: "필수입니다.",
									validate: (value) => {
										const dateObj = moment(value);
										// console.log(dateObj, moment());
										if (dateObj.isBefore(moment())) {
											return "시작일 이전일 수 없습니다.";
										} else {
											return undefined;
										}
									},
								}}
								render={({
									field: { ref, value, onChange, ...field },
									fieldState: { error },
								}) => (
									<DatePicker
										{...field}
										onChange={(value) => {
											if (value) {
												onChange(value?.toDate());
											}
										}}
										minDate={moment()}
										format="YYYY.MM.DD"
										slotProps={{
											textField: {
												variant: "outlined",
												error: !!error,
												helperText: error?.message,
												autoFocus: true,
												inputRef: ref,
												fullWidth: true,
												sx: {
													paddingRight: 10,
												},
											},
										}}
										value={moment(value)}
									/>
								)}
							/>
						</LocalizationProvider>
						<div className="description">정부/민간 지원금 대상 여부를 알려주세요.</div>
						<div className="description-mini">
							{`• 지원금을 사용하는 경우 제안서 수락 후 영업일 2일 이내 담당 매니저가 별도로 연락드립니다.`}
						</div>
						<EmptySpace space={10} />
						<Controller
							name="useSupportFund"
							control={control}
							rules={{
								validate: (value) => {
									if (value === null) {
										return "필수값입니다";
									} else {
										return undefined;
									}
								},
							}}
							defaultValue={null}
							render={({
								field: { ref, onChange, onBlur, ...field },
								fieldState: { error },
							}) => (
								<>
									<div className="button-group" ref={ref}>
										<Button
											variant={useSupportFund ? "contained" : "outlined"}
											onClick={() => onChange(true)}
											onBlur={onBlur}
										>
											네, 지원금 대상으로 사용 예정이에요
										</Button>
										<Button
											variant={
												useSupportFund === false ? "contained" : "outlined"
											}
											onClick={() => onChange(false)}
											onBlur={onBlur}
										>
											아니요, 대상이 아니거나 사용하지 않아요
										</Button>
									</div>
									{error?.message && (
										<FormHelperText error>{error?.message}</FormHelperText>
									)}
								</>
							)}
						/>
						{useSupportFund && (
							<>
								<EmptySpace space={20} />
								<div className="description-mini">
									{`• 지원금은 집행처에 따라 사용 가능 여부와 방법이 상이합니다.\n• 사전에 집행처에 사용 가능 여부를 확인하셔야합니다.`}
								</div>
								<EmptySpace space={10} />
								<Controller
									name="understandSupportFund"
									control={control}
									rules={{
										validate: (value) => {
											if (value === false) {
												return "필수값입니다";
											} else {
												return undefined;
											}
										},
									}}
									defaultValue={false}
									render={({
										field: { ref, onChange, onBlur, ...field },
										fieldState: { error },
									}) => (
										<>
											<div className="button-group" ref={ref}>
												<Button
													variant={
														watch("understandSupportFund")
															? "contained"
															: "outlined"
													}
													onClick={() =>
														onChange(!watch("understandSupportFund"))
													}
													onBlur={onBlur}
												>
													네, 이해했어요
												</Button>
											</div>
											{error?.message && (
												<FormHelperText error>
													{error?.message}
												</FormHelperText>
											)}
										</>
									)}
								/>
								<div className="description">
									해당되는 지원금 종류와 집행처를 알려주세요
								</div>
								<EmptySpace space={10} />
								<Controller
									name="supportInfo"
									control={control}
									rules={{ required: "필수값입니다" }}
									defaultValue={null}
									render={({
										field: { ref, ...field },
										fieldState: { error },
									}) => (
										<TextField
											fullWidth
											inputRef={ref}
											{...field}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							sx={{
								borderRadius: 2,
								width: "150px",
								fontWeight: "bold",
								fontSize: "16px",
							}}
							variant="contained"
							type="submit"
						>
							제출하기
						</Button>
					</DialogActions>
				</form>
			</ProposalAcceptDialog>

			<OnePointDialog
				maxWidth={"sm"}
				fullWidth
				open={openRejectModal}
				onClose={() => {
					setOpenRejectModal(false);
				}}
			>
				<DialogTitle>
					<div>
						<div className="helptext">
							본 업무 범위 제안을 거절하시는 경우, 번복이 어렵습니다.
						</div>
						<div className="title">{`${proposal.expert}님과\n업무를 진행하지 않는 이유를 알려주세요`}</div>
					</div>
				</DialogTitle>
				<form
					onSubmit={rejectHandleOnSubmit(
						(data) => {
							mutateHandleOnClickRejectByClient({
								matchedExpertDataUuid: matchedExpertDataUuid,
								reason:
									data.formResponse === "기타"
										? "기타: " + data.etc
										: data.formResponse,
							});
						},
						(error) => {
							// console.log(error);
						},
					)}
				>
					<DialogContent>
						<div style={{ flexDirection: "column" }}>
							<FormControl fullWidth>
								<Controller
									name={`formResponse`}
									control={controlReject}
									rules={{ required: "필수 입력 항목입니다." }}
									render={({
										field: { ref, ...field },
										fieldState: { error },
									}) => (
										<>
											{error && (
												<FormHelperText error>
													{error?.message}
												</FormHelperText>
											)}
											<RadioGroup {...field}>
												{rejectReason.map((reason, index) => (
													<>
														<FormControlLabel
															key={
																"radio-formresponse-" +
																index.toString()
															}
															control={<Radio />}
															label={reason}
															value={reason}
														/>
													</>
												))}
											</RadioGroup>
										</>
									)}
								></Controller>
								{watchReject("formResponse") === "기타" && (
									<Controller
										name={`etc`}
										control={controlReject}
										rules={{ required: "필수 입력 항목입니다." }}
										render={({
											field: { ref, ...field },
											fieldState: { error },
										}) => (
											<>
												<TextField
													fullWidth
													multiline
													minRows={2}
													placeholder="사유를 입력해주세요"
													inputRef={ref}
													{...field}
													error={!!error}
													helperText={error?.message}
												/>
											</>
										)}
									/>
								)}
							</FormControl>
						</div>
					</DialogContent>

					<DialogActions
						style={{
							marginBottom: "16px",
						}}
					>
						{/* {isLastOne && (
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "space-between",
									width: "100%",
									alignItems: "center",
								}}
							>
								<div
									style={{
										border: "1px solid var(--primary)",
										borderRadius: "20px",
										marginLeft: "16px",
										padding: "8px 12px",
										fontWeight: "bold",
										fontSize: "16px",
										width: "fit-content",
										color: "var(--primary)",
										cursor: "pointer",
									}}
									onClick={() => {
										window.open(
											`https://form.typeform.com/to/Yb9ONCsw#name${proposal.client}-${proposal.expert}`,
										);
									}}
								>
									3분 설문조사 참여하고 5만원 크레딧 받기
								</div>
							</div>
						)} */}
						<LoadingButton
							sx={{
								borderRadius: 2,
								fontWeight: "bold",
								fontSize: "16px",
							}}
							variant="outlined"
							onClick={() => {
								setOpenRejectModal(false);
							}}
						>
							닫기
						</LoadingButton>
						<LoadingButton
							sx={{
								borderRadius: 2,
								fontWeight: "bold",
								fontSize: "16px",
								width: "150px",
							}}
							variant="contained"
							loading={isLoadingHandleOnClickRejectByClient}
							type="submit"
						>
							거절하기
						</LoadingButton>
					</DialogActions>
				</form>
			</OnePointDialog>

			{/* depositUuid가 존재하는 경우만 환불 완료 모달 표시 */}
			{depositStatus?.depositUuid && (
				<OnePointDialog
					open={openDepositRefundModal}
					onClose={() => setOpenDepositRefundModal(false)}
					maxWidth="sm"
					fullWidth
					sx={{
						"& .MuiDialog-paper": {
							borderRadius: "18px",
						}
					}}
				>
					<DialogTitle
						sx={{
							backgroundColor: "var(--primary)",
							color: "white",
							textAlign: "center",
							padding: "32px 24px 24px"
						}}
					>
						<div style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "12px"
						}}>
							<div style={{
								fontSize: "24px",
								fontWeight: "700"
							}}>
								서비스 보증금 환불 안내
							</div>
						</div>
					</DialogTitle>
					<DialogContent sx={{ padding: "48px 32px 40px" }}>
						<div style={{ 
							textAlign: "center",
							lineHeight: "1.7"
						}}>
							<div style={{
								fontSize: "16px",
								fontWeight: "600",
								marginTop: "32px",
								marginBottom: "24px",
								color: "#333",
								lineHeight: "1.5"
							}}>
								서비스 보증금 환불 요청이 완료되었습니다.
							</div>
							<div style={{
								fontSize: "16px",
								color: "#666",
								marginBottom: "8px",
								lineHeight: "1.6"
							}}>
								담당자가 3일 이내에 환불해드리도록 하겠습니다.
							</div>
							<div style={{
								fontSize: "16px",
								color: "#666",
								lineHeight: "1.6"
							}}>
								문의사항이 있으실 경우 채널톡으로 문의 부탁드립니다.
							</div>
						</div>
					</DialogContent>
					<DialogActions sx={{ padding: "0 32px 32px" }}>
						<Button
							onClick={() => {
								setOpenDepositRefundModal(false);
								router.replace("/match/my");
							}}
							variant="contained"
							fullWidth
							sx={{
								backgroundColor: "var(--primary)",
								color: "white",
								fontWeight: "bold",
								padding: "14px",
								borderRadius: "12px",
								fontSize: "16px",
								"&:hover": {
									backgroundColor: "var(--primary)",
									opacity: 0.9
								}
							}}
						>
							확인
						</Button>
					</DialogActions>
				</OnePointDialog>
			)}

			{/* AI 후보자 분석 피드백 모달 */}
			<Dialog
				open={openAiFeedbackModal}
				onClose={() => setOpenAiFeedbackModal(false)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { borderRadius: "16px" } }}
			>
				<DialogTitle sx={{ textAlign: "center", pt: 3, pb: 1 }}>
					<AutoAwesomeRoundedIcon sx={{ color: "#7c3aed", fontSize: 28, mb: 0.5 }} />
					<Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
						AI 후보자 분석이 도움이 되셨나요?
					</Typography>
				</DialogTitle>
				<DialogContent sx={{ textAlign: "center", px: 3, pb: 1 }}>
					{!aiFeedbackSubmitted ? (
						<>
							<Rating
								value={aiFeedbackRating}
								onChange={(_, value) => setAiFeedbackRating(value)}
								precision={0.5}
								size="large"
								sx={{
									mt: 1,
									mb: 2,
									"& .MuiRating-iconFilled": { color: "#7c3aed" },
									"& .MuiRating-iconHover": { color: "#6d28d9" },
								}}
							/>
							<TextField
								value={aiFeedbackComment}
								onChange={(e) => setAiFeedbackComment(e.target.value)}
								placeholder="의견을 남겨주세요 (선택사항)"
								multiline
								rows={2}
								fullWidth
								variant="outlined"
								sx={{
									mb: 1,
									"& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 14 },
								}}
							/>
						</>
					) : (
						<Typography sx={{ py: 3, fontSize: 15, color: "#374151" }}>
							피드백을 보내주셔서 감사합니다!
						</Typography>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
					{!aiFeedbackSubmitted ? (
						<>
							<Button
								fullWidth
								onClick={() => setOpenAiFeedbackModal(false)}
								sx={{ borderRadius: "10px", color: "#6b7280" }}
							>
								건너뛰기
							</Button>
							<Button
								fullWidth
								variant="contained"
								disabled={!aiFeedbackRating}
								onClick={async () => {
									if (!aiFeedbackRating) return;
									try {
										await sendProposalAssistantFeedback({
											matchUuid: proposal.matchUuid,
											rating: aiFeedbackRating,
											comment: aiFeedbackComment || undefined,
										});
										setAiFeedbackSubmitted(true);
										setTimeout(() => setOpenAiFeedbackModal(false), 1500);
									} catch {
										setOpenAiFeedbackModal(false);
									}
								}}
								sx={{
									borderRadius: "10px",
									fontWeight: 700,
									backgroundColor: "#7c3aed",
									"&:hover": { backgroundColor: "#6d28d9" },
								}}
							>
								제출하기
							</Button>
						</>
					) : (
						<Button
							fullWidth
							variant="contained"
							onClick={() => setOpenAiFeedbackModal(false)}
							sx={{
								borderRadius: "10px",
								fontWeight: 700,
								backgroundColor: "#7c3aed",
								"&:hover": { backgroundColor: "#6d28d9" },
							}}
						>
							닫기
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ProposalView;

const Row = styled.div`
	display: flex;
	flex-direction: row;
	align-content: flex-end;
	gap: 8px;
	align-items: center;
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const ProposalAcceptDialog = styled(Dialog)`
	.MuiPaper-root {
		border-radius: 18px;
	}
	.MuiDialogTitle-root {
		background-color: var(--primary);
		color: white;

		.title-box {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			align-items: center;

			.title {
				font-size: 30px;
				font-style: normal;
				font-weight: 700;
			}

			.icon {
				width: 60px;
				height: 60px;

				.logo {
					width: 100%;
				}
			}
		}
	}

	.button-group {
		display: flex;
		flex-direction: row;
		gap: 10px;
		flex-wrap: wrap;
	}

	.description {
		font-size: 20px;
		font-weight: bold;
		margin-top: 30px;

		.blue {
			color: var(--primary);
		}
	}

	.description-mini {
		font-size: 16px;
	}

	@media screen and (max-width: 600px) {
		.description-mini {
			display: none;
		}
	}
`;

const BlueText = styled.div`
	font-size: 20px;
	font-style: normal;
	font-weight: bold;
	line-height: normal;
	color: rgb(8, 126, 255);
`;

export const CustomButton = styled(Button)`
	height: 48px;
	background-color: var(--primary);
	border: none;
	font-size: 24px;
	font-weight: bold;
	border-radius: 16px;
	color: white;
	transition: all 0.2s ease;

	&:hover {
		color: white !important;
		filter: brightness(0.9);
	}

	@media screen and (max-width: 768px) {
		font-size: 16px;
		padding: 3px 8px;
	}
`;

export const NegotiationButton = styled(Button)`
	min-height: 52px;
	font-size: 15px;
	font-weight: 600;
	border-radius: 12px;
	border: 1.5px solid #d1d5db;
	color: #4b5563;
	background-color: #ffffff;
	transition: all 0.2s ease;

	&:hover {
		color: #374151 !important;
		background-color: #f3f4f6 !important;
		border-color: #9ca3af !important;
	}

	@media screen and (max-width: 768px) {
		font-size: 14px;
	}
`;
